import React, { useState, useEffect, useRef } from 'react';
/* Fix: Import from react-router instead of react-router-dom for v7 unified package support */
import { Link, useNavigate, useLocation } from 'react-router';
import { HelpCircle, BarChart3, Moon, Sun, ChevronLeft, Clock, Calendar, X, Eye, Search, Share2, CircleDashed, Square, Sparkles } from 'lucide-react';
import { TRANSLATIONS, SUPPORTED_LANGS, SLUGS } from '../constants';
import { SupportedLang, Theme, UserStats } from '../types';
import { useSmartBack, useLanguageNavigate } from '../App';
import { getTimeUntilNextMidnight } from '../utils/gameUtils';

const Modal: React.FC<{ isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode; isTutorial?: boolean }> = ({ isOpen, onClose, title, children, isTutorial }) => {
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-midnight/95 animate-fade-in backdrop-blur-sm">
      <div className={`bg-off-white dark:bg-[#1A222C] w-full max-w-md lg:max-w-[500px] max-h-[90vh] lg:max-h-[85vh] rounded-3xl shadow-2xl relative border border-zinc-200 dark:border-zinc-800 transition-transform duration-500 ease-out flex flex-col overflow-hidden ${isTutorial ? 'animate-scale-in' : ''}`}>
        {/* Fixed Header & Close Button */}
        <div className="p-8 sm:p-12 lg:p-8 pb-4 sm:pb-4 lg:pb-2 shrink-0 flex items-center justify-between">
          <h2 className="text-4xl lg:text-3xl font-heading tracking-widest text-ink dark:text-off-white uppercase">{title}</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-ink dark:hover:text-off-white transition-colors p-2">
            <X className="w-6 h-6 lg:w-5 lg:h-5" />
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div className="p-8 sm:p-12 lg:p-8 pt-0 sm:pt-0 lg:pt-0 overflow-y-auto flex-1 custom-scrollbar">
          {children}
        </div>
      </div>
    </div>
  );
};

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleBack, lang, prefix, pathParts } = useSmartBack();
  const langNavigate = useLanguageNavigate();
  const [theme, setTheme] = useState<Theme>(() => (localStorage.getItem('theme') as Theme) || 'dark');
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [countdown, setCountdown] = useState(getTimeUntilNextMidnight());
  const [stats, setStats] = useState<UserStats>({
    played: 0, wins: 0, currentStreak: 0, maxStreak: 0, winDistribution: [0, 0, 0, 0, 0, 0]
  });
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isStatsOpen) {
      const raw = localStorage.getItem('movie-riddle-stats');
      if (raw) setStats(JSON.parse(raw));
    }
  }, [isStatsOpen]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setIsLangOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (isStatsOpen) {
      interval = setInterval(() => {
        setCountdown(getTimeUntilNextMidnight());
      }, 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [isStatsOpen]);

  const changeLang = (newLang: SupportedLang) => {
    const parts = location.pathname.split('/').filter(Boolean);
    const oldLang = SUPPORTED_LANGS.includes(parts[0] as SupportedLang) ? (parts[0] as SupportedLang) : 'en';
    const slugPart = oldLang === 'en' ? parts[0] : parts[1];
    
    let key = 'home';
    if (slugPart) {
      key = Object.keys(SLUGS[oldLang]).find(k => SLUGS[oldLang][k] === slugPart) || slugPart;
    }

    const newPrefix = newLang === 'en' ? '' : `/${newLang}`;
    const newSlug = SLUGS[newLang][key] !== undefined ? SLUGS[newLang][key] : key;
    const cleanSlug = newSlug === '' ? '' : `/${newSlug}`;
    
    const subSlug = oldLang === 'en' ? parts[1] : parts[2];
    const actionSlug = oldLang === 'en' ? parts[2] : parts[3];
    
    const mid = subSlug ? `/${subSlug}` : '';
    const end = (actionSlug && SLUGS[oldLang].solution === actionSlug) ? `/${SLUGS[newLang].solution}` : (actionSlug ? `/${actionSlug}` : '');
    
    const finalPath = `${newPrefix}${cleanSlug}${mid}${end}` || '/';
    navigate(finalPath);
    setIsLangOpen(false);
  };

  const pathSegments = pathParts.length > 0 
    ? (SUPPORTED_LANGS.includes(pathParts[0] as any) ? pathParts.slice(1) : pathParts) 
    : [];

  const pageSegment = pathSegments[0] || '';
  const showHeaderBack = pageSegment === SLUGS[lang].daily || pageSegment === SLUGS[lang].infinite;

  const logoLink = prefix || '/';
  const winPercentage = stats.played > 0 ? Math.round((stats.wins / stats.played) * 100) : 0;

  return (
    <>
      <header className="sticky top-0 z-50 w-full bg-off-white/95 dark:bg-midnight/95 backdrop-blur-md border-b border-zinc-200 dark:border-zinc-800 transition-colors h-14 sm:h-16 lg:h-14">
        <div className="max-w-[1200px] mx-auto h-full px-4 sm:px-6 flex items-center justify-between">
          <Link to={logoLink} className="text-lg sm:text-2xl lg:text-xl font-heading tracking-wide uppercase flex items-center gap-1.5 sm:gap-2 group truncate">
            <span className="bg-prestige-gold text-midnight px-1.5 sm:px-1.5 rounded-sm transition-transform group-hover:scale-105 shrink-0">MOVIE</span>
            <span className="text-ink dark:text-off-white shrink-0">RIDDLE</span>
          </Link>

          <div className="flex items-center gap-1 sm:gap-4 lg:gap-3">
            {showHeaderBack && (
              <button onClick={handleBack} className="p-2 text-zinc-500 dark:text-zinc-400 hover:text-ink dark:hover:text-off-white transition-all flex items-center gap-1 sm:gap-2 font-bold text-[8px] sm:text-[9px] tracking-[0.2em]">
                <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={3} />
                <span className="hidden sm:inline">{TRANSLATIONS.backLabel[lang]}</span>
              </button>
            )}

            <div className="flex items-center">
              <button onClick={() => langNavigate('archive')} className="p-2 text-zinc-500 dark:text-zinc-400 hover:text-ink dark:hover:text-off-white transition-colors" title={TRANSLATIONS.archive[lang]}>
                <Calendar className="w-4 h-4 sm:w-4 sm:h-4 lg:w-3.5 lg:h-3.5" strokeWidth={2.5} />
              </button>
              <button onClick={() => setIsTutorialOpen(true)} className="p-2 text-zinc-500 dark:text-zinc-400 hover:text-ink dark:hover:text-off-white transition-colors" title={TRANSLATIONS.helpTooltip[lang]}>
                <HelpCircle className="w-4 h-4 sm:w-4 sm:h-4 lg:w-3.5 lg:h-3.5" strokeWidth={2.5} />
              </button>
              <button onClick={() => setIsStatsOpen(true)} className="p-2 text-zinc-500 dark:text-zinc-400 hover:text-ink dark:hover:text-off-white transition-colors" title={TRANSLATIONS.statsTooltip[lang]}>
                <BarChart3 className="w-4 h-4 sm:w-4 sm:h-4 lg:w-3.5 lg:h-3.5" strokeWidth={2.5} />
              </button>
            </div>

            <div className="relative h-10 flex items-center" ref={langRef}>
              <button onClick={() => setIsLangOpen(!isLangOpen)} className="uppercase text-[9px] sm:text-[10px] lg:text-[9px] font-extrabold tracking-widest text-zinc-500 dark:text-zinc-400 hover:text-ink dark:hover:text-off-white px-1.5 sm:px-2.5 h-full flex items-center border border-zinc-200 dark:border-zinc-800 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-colors">
                {lang}
              </button>
              {isLangOpen && (
                <div className="absolute right-0 top-full mt-2 bg-off-white dark:bg-surface border border-zinc-200 dark:border-zinc-800 rounded-xl overflow-hidden shadow-2xl py-1 w-20 animate-fade-in z-50">
                  {SUPPORTED_LANGS.map(l => (
                    <button key={l} onClick={() => changeLang(l)} className={`w-full py-2 px-3 text-left uppercase text-[9px] font-extrabold hover:bg-zinc-200 dark:hover:bg-midnight transition-colors ${lang === l ? 'text-prestige-gold' : 'text-zinc-500 dark:text-zinc-400'}`}>
                      {l}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')} className="p-2 text-zinc-500 dark:text-zinc-400 hover:text-ink dark:hover:text-off-white transition-colors h-10 w-10 flex items-center justify-center border border-zinc-200 dark:border-zinc-800 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-900" title={TRANSLATIONS.themeTooltip[lang]}>
              {theme === 'dark' ? <Sun className="w-4 h-4 lg:w-3.5 lg:h-3.5" /> : <Moon className="w-4 h-4 lg:w-3.5 lg:h-3.5" />}
            </button>
          </div>
        </div>
      </header>

      <Modal isOpen={isTutorialOpen} onClose={() => setIsTutorialOpen(false)} title={TRANSLATIONS.tutorialTitle[lang]} isTutorial>
        <div className="space-y-6 sm:space-y-10 lg:space-y-5">
          <div className="space-y-5 sm:space-y-8 lg:space-y-4">
            <div className="bg-[#242D38] p-5 lg:p-4 rounded-2xl flex gap-5 lg:gap-4 border border-white/5 items-start">
              <div className="bg-prestige-gold p-2.5 lg:p-2 rounded-xl text-midnight shrink-0">
                <Eye className="w-5 h-5 lg:w-4 lg:h-4" strokeWidth={2.5} />
              </div>
              <div className="space-y-1">
                <h3 className="font-heading text-xl lg:text-lg tracking-wider text-off-white leading-none">{TRANSLATIONS.tutorialStep1Title[lang]}</h3>
                <p className="text-zinc-400 text-xs lg:text-[11px] font-sans leading-relaxed">{TRANSLATIONS.tutorialStep1Desc[lang]}</p>
              </div>
            </div>

            <div className="bg-[#242D38] p-5 lg:p-4 rounded-2xl flex gap-5 lg:gap-4 border border-white/5 items-start">
              <div className="bg-prestige-gold p-2.5 lg:p-2 rounded-xl text-midnight shrink-0">
                <Search className="w-5 h-5 lg:w-4 lg:h-4" strokeWidth={2.5} />
              </div>
              <div className="space-y-1">
                <h3 className="font-heading text-xl lg:text-lg tracking-wider text-off-white leading-none">{TRANSLATIONS.tutorialStep2Title[lang]}</h3>
                <p className="text-zinc-400 text-xs lg:text-[11px] font-sans leading-relaxed">{TRANSLATIONS.tutorialStep2Desc[lang]}</p>
              </div>
            </div>

            <div className="bg-[#242D38] p-5 lg:p-4 rounded-2xl flex gap-5 lg:gap-4 border border-white/5 items-start">
              <div className="bg-prestige-gold p-2.5 lg:p-2 rounded-xl text-midnight shrink-0">
                <Share2 className="w-5 h-5 lg:w-4 lg:h-4" strokeWidth={2.5} />
              </div>
              <div className="space-y-1">
                <h3 className="font-heading text-xl lg:text-lg tracking-wider text-off-white leading-none">{TRANSLATIONS.tutorialStep3Title[lang]}</h3>
                <p className="text-zinc-400 text-xs lg:text-[11px] font-sans leading-relaxed">{TRANSLATIONS.tutorialStep3Desc[lang]}</p>
              </div>
            </div>
          </div>

          <div className="space-y-4 lg:space-y-3">
            <h4 className="text-center font-heading text-sm lg:text-xs text-zinc-500 tracking-[0.2em] uppercase">FRAME PROGRESSION</h4>
            <div className="flex justify-between items-center px-4 sm:px-4 lg:px-2">
              {[1, 2, 3, 4, 5, 6].map(num => (
                <div key={num} className="flex flex-col items-center gap-2">
                  <div className={`w-10 h-10 lg:w-8 lg:h-8 rounded-xl lg:rounded-lg border flex items-center justify-center transition-all duration-700 ${
                    num === 1 ? 'border-zinc-700 bg-zinc-800/40 text-zinc-500' :
                    num === 3 ? 'border-zinc-600 bg-zinc-700/40 text-zinc-400' :
                    num === 6 ? 'border-prestige-gold bg-prestige-gold/20 text-prestige-gold' :
                    'border-zinc-800 bg-transparent text-zinc-600'
                  }`}>
                    {num === 1 && <CircleDashed className="w-5 h-5 lg:w-4 lg:h-4 animate-spin-slow" strokeWidth={1.5} />}
                    {num === 3 && <Square className="w-5 h-5 lg:w-4 lg:h-4 opacity-50" strokeWidth={1.5} />}
                    {num === 6 && <Sparkles className="w-5 h-5 lg:w-4 lg:h-4 animate-pulse" strokeWidth={2} />}
                    {num !== 1 && num !== 3 && num !== 6 && <div className="w-1.5 h-1.5 rounded-full bg-current" />}
                  </div>
                  <span className="text-[10px] lg:text-[9px] font-bold text-zinc-600 font-sans tracking-tighter">F{num}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-6 sm:pt-6 lg:pt-4 border-t border-zinc-800">
            <p className="text-center text-[10px] sm:text-[11px] lg:text-[9px] font-bold text-zinc-500 uppercase tracking-widest leading-relaxed sm:leading-loose max-w-[280px] lg:max-w-full mx-auto italic">
              {TRANSLATIONS.tutorialNote[lang]}
            </p>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isStatsOpen} onClose={() => setIsStatsOpen(false)} title={TRANSLATIONS.statsTitle[lang]}>
        <div className="flex items-center justify-between mb-12 text-center lg:mb-8">
          <div className="flex-1">
            <div className="text-4xl lg:text-3xl font-heading text-ink dark:text-off-white mb-1">{stats.played}</div>
            <div className="text-[10px] uppercase font-bold text-zinc-500 dark:text-zinc-400 tracking-[0.1em]">{TRANSLATIONS.statsPlayed[lang]}</div>
          </div>
          <div className="flex-1">
            <div className="text-4xl lg:text-3xl font-heading text-ink dark:text-off-white mb-1">{winPercentage}</div>
            <div className="text-[10px] uppercase font-bold text-zinc-500 dark:text-zinc-400 tracking-[0.1em]">{TRANSLATIONS.statsWins[lang]}</div>
          </div>
          <div className="flex-1">
            <div className="text-4xl lg:text-3xl font-heading text-ink dark:text-off-white mb-1">{stats.currentStreak}</div>
            <div className="text-[10px] uppercase font-bold text-zinc-500 dark:text-zinc-400 tracking-[0.1em]">{TRANSLATIONS.statsStreak[lang]}</div>
          </div>
          <div className="flex-1">
            <div className="text-4xl lg:text-3xl font-heading text-ink dark:text-off-white mb-1">{stats.maxStreak}</div>
            <div className="text-[10px] uppercase font-bold text-zinc-500 dark:text-zinc-400 tracking-[0.1em]">{TRANSLATIONS.statsMaxStreak[lang]}</div>
          </div>
        </div>
        
        <div className="space-y-4 mb-14 lg:mb-10 lg:space-y-3">
          {stats.winDistribution.map((count, i) => {
            const barWidth = stats.wins > 0 ? Math.max(5, (count / stats.wins) * 100) : 5;
            return (
              <div key={i} className="flex items-center gap-6 lg:gap-4">
                <span className="w-4 text-xs font-bold text-zinc-500 dark:text-zinc-400">{i + 1}</span>
                <div className="flex-1 h-5 lg:h-4 bg-[#0B0E14] rounded-sm overflow-hidden">
                  <div 
                    className={`h-full flex items-center justify-end px-3 text-[10px] font-bold transition-all duration-1000 ${count > 0 ? 'bg-prestige-gold text-midnight' : 'bg-zinc-800'}`} 
                    style={{ width: `${barWidth}%` }}
                  >
                    {count > 0 ? count : ''}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="border-t border-zinc-200 dark:border-zinc-800 pt-10 lg:pt-6 text-center">
          <div className="text-[11px] lg:text-[10px] font-black text-zinc-500 dark:text-zinc-400 tracking-[0.3em] mb-5 lg:mb-3 flex items-center justify-center gap-2">
            <Clock className="w-4 h-4 text-prestige-gold" />
            {TRANSLATIONS.nextReelIn[lang]}
          </div>
          <div className="text-5xl lg:text-4xl font-heading tracking-widest text-ink dark:text-off-white tabular-nums">
            {countdown}
          </div>
        </div>
      </Modal>

      <style>{`
        .animate-spin-slow {
          animation: spin 3s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #374151;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #4B5563;
        }
      `}</style>
    </>
  );
};

export default Header;